import {IAuthData} from '@/model/IAuthData';
import {IConfig} from '@/model/config/IConfig';
import {IApplication} from "@/model/config/IApplication";
import {registerApplication, start, unregisterApplication} from 'single-spa';
import {IMenuConfig} from "@/model/menu/IMenuConfig";
import store from '@/store';
import router from '@/router';
import {RouteConfig} from "vue-router";
import {IMenuEntity} from "@/model/menu/IMenuEntity";
import { Fetch } from 'water-rest/src/fetch/Fetch';
import {getCoordinates, getMap, triggerMapSettingsUpdate} from "@/services/map-service";
import {searchByFiasAddress, searchAddressByCoords} from "@/services/nominatim-service";
import {IOnDemandApplication} from "@/model/config/IOnDemandApplication";
import {clearAuthState, isMenuAvailable} from "@/services/auth-service";
import {IMenuBaseEntity} from "@/model/menu/IMenuBaseEntity";
import {SharedStore} from "water-rest/src/model/sharedStore/SharedStore";
import {subscribeToStomp, unsubscribe} from "@/services/stomp-service";
import GlobalStore from "water-rest/src/store/GlobalStore";
import {
    geocodeByAddressDadata,
    geocodeByAddressYandex,
    geocodeByIdDadata,
    searchAddressByCoordsDD,
    searchByDataAddressInfo,
    searchByFiasAddressDD,
    searchByFiasAddressIdDD,
} from "@/services/dadata-service";
import {setGlobalThemeType, ThemeType} from "@/services/theme-service";

const requestConfig = (): Promise<IConfig> => {
    if (process.env.NODE_ENV === 'production')
        return Fetch.get<IConfig>(`${Fetch.buildAuthModuleUrl()}/config`);
    else
        return Fetch.get<IConfig>('/config.json');
};

const loadApp = (url: string, authData: IAuthData): Promise<any> => {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `${url}?Authorization=${authData.accessToken}`;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

const processSubMenuRoutes = (menu: IMenuBaseEntity, newRoutes: RouteConfig[], authData: IAuthData | undefined) => {
    if ( menu.subMenus && menu.subMenus.length > 0 ) {
        menu.subMenus.forEach( (eachSubMenu) => {
            if ( isMenuAvailable( eachSubMenu, authData ) ) {
                const { path, name } = eachSubMenu;
                if ( path && path.length > 0 && name )
                    newRoutes.push({ path, name });

                processSubMenuRoutes(eachSubMenu, newRoutes, authData);
            }
        } );
    }
}

const processMenuRoutes = (menus: IMenuEntity[], newRoutes: RouteConfig[], authData: IAuthData) => {
    menus.forEach( (eachMenu) => {
        if ( isMenuAvailable( eachMenu, authData ) ) {
            const { path, name } = eachMenu;
            if ( path && path.length > 0 && name )
                newRoutes.push({ path, name });

            processSubMenuRoutes(eachMenu, newRoutes, authData);

            if ( eachMenu.routes && eachMenu.routes.length > 0 ) {
                eachMenu.routes.forEach( (eachRoute) => {
                    if ( isMenuAvailable( eachRoute, authData ) ) {
                        const { path, name } = eachRoute;
                        if ( path && path.length > 0 && name )
                            newRoutes.push({ path, name });
                    }
                } );
            }
        }
    } );
};

const processRoutes = (menu: IMenuConfig, authData: IAuthData) => {
    const newRoutes: RouteConfig[] = [];

    if ( menu.menus && menu.menus.length > 0 )
        processMenuRoutes( menu.menus, newRoutes, authData );

    if ( menu.sharedMenus && menu.sharedMenus.length > 0 )
        processMenuRoutes( menu.sharedMenus, newRoutes, authData );

    if ( menu.routes && menu.routes.length > 0 ) {
        menu.routes.forEach( (eachRoute) => {
            const { path, name } = eachRoute;
            if ( path && path.length > 0 && name )
                newRoutes.push({ path, name });
        } );
    }

    router.addRoutes( newRoutes );
};

const processMenu = (menu: IMenuConfig, authData: IAuthData) => {
    store.commit('addMenu', menu);
    processRoutes(menu, authData);
};

export const sortMenuItems = (items: IMenuBaseEntity[]) => {
    items.sort( (a, b) => { // Сортировка по полю sortOrder и title
        if (a.sortOrder < b.sortOrder) return -1;
        if (a.sortOrder > b.sortOrder) return 1;
        return a.title.localeCompare(b.title);
    });
}

/* --- ON DEMAND SECTION --- */
const getOnDemandApplication = (name: string): IOnDemandApplication | undefined => {
    return store.getters.getOnDemandApplications.find( (eachApplication: IOnDemandApplication) => eachApplication.name === name );
};

const registerOnDemandMicroFront = (onDemandApplication: IOnDemandApplication, route: string, params: any): Promise<any> => {
    console.log('REGISTERING...', location.pathname, route);
    // @ts-ignore
    const lib = window[`lib_${onDemandApplication.name}`];
    registerApplication(
        onDemandApplication.name,
        lib,
        location => {
            return location.pathname === route
        },
        {
            ...params
        }
    );

    onDemandApplication.loading = false;
    onDemandApplication.loaded = true;
    onDemandApplication.unloaded = false;
    onDemandApplication.onLoad.next();
    start();
    return Promise.resolve();
};

export const loadOnDemandMicroFront = (name: string, route: string, params: any): Promise<any> => {
    console.log('REQUESTING: ', name, route, params);
    const onDemandApplication: IOnDemandApplication | undefined = getOnDemandApplication(name);
    if ( !onDemandApplication ) return Promise.reject();
    console.log('APPLICATION: ', onDemandApplication);

    return new Promise<any>( (resolve, reject) => {
        if ( onDemandApplication.loading ) {
            onDemandApplication.onLoad.subscribe( () => {
                resolve(null);
            }, () => {
                reject();
            } )
        }
        else if ( onDemandApplication.rejected )
            reject();
        else if ( onDemandApplication.loaded && !onDemandApplication.unloaded )
            resolve(null);
        else if ( onDemandApplication.unloaded )
            return registerOnDemandMicroFront(onDemandApplication, route, params)
        else {
            console.log('LOADING...');
            onDemandApplication.loading = true;
            loadApp( onDemandApplication.src, store.getters.authData )
                .then( () => registerOnDemandMicroFront(onDemandApplication, route, params).then( () => resolve(null) ) )
                .catch( (error) => {
                    onDemandApplication.loading = false;
                    onDemandApplication.rejected = true;
                    onDemandApplication.onLoad.error(error);
                    reject();
                } )
        }
    } );
};

export const unloadOnDemandMicroFront = (name: string): Promise<any> => {
    console.log('UNLOADING: ', name);
    const onDemandApplication: IOnDemandApplication | undefined = getOnDemandApplication(name);
    if ( !onDemandApplication ) return Promise.reject();

    return new Promise<any>( (resolve, reject) => {
        unregisterApplication(name)
            .then( () => {
                onDemandApplication.unloaded = true;
                resolve(null);
            } )
            .catch( () => {
                reject();
            } )
    } )
};
/* --- /ON DEMAND SECTION --- */

const prepareContainer = (name: string) => {
    const modulesContainer = document.getElementById('modules-container');
    const moduleContainer = document.createElement('div');
    moduleContainer.setAttribute('id', `single-spa-application:${name}`);
    modulesContainer && modulesContainer.append(moduleContainer);
}
const registerMicroFront = (authData: IAuthData, application: IApplication, config: IConfig, redirect: boolean) => {
    loadApp(application.src, authData)
        .then(() => {
            prepareContainer(application.name);
            // @ts-ignore
            const lib = window[`lib_${application.name}`];
            registerApplication(
                application.name,
                lib,
                location => {
                    return location.pathname.startsWith(`${application.route}`)
                },
                {
                    sharedStore: SharedStore,
                    globalStore: GlobalStore,
                    authData: store.getters.authData,
                    getMap: getMap,
                    getCoordinates,
                    loadOnDemandMicroFront,
                    triggerMapSettingsUpdate,
                    searchByFiasAddress,
                    searchAddressByCoords,
                    searchByFiasAddressDD,
                    searchByFiasAddressIdDD,
                    searchAddressByCoordsDD,
                    searchByDataAddressInfo,
                    geocodeByIdDadata,
                    geocodeByAddressDadata,
                    geocodeByAddressYandex,
                    unloadOnDemandMicroFront,
                    logout: clearAuthState,
                    subscribeToStomp,
                    unsubscribeFromStomp: unsubscribe,
                    basePath: config.common.basePath,
                    systems: config.common.systems,
                    region: config.common.region || 18,
                    additionalRegions: config.common.additionalRegions || [],
                    environment: config.common.environment,
                }
            );

            if ( typeof lib.getMenu === 'function' )
                processMenu( lib.getMenu(), authData );

            store.commit('incrementProcessedServices', redirect);
        })
        .catch((error) => {
            // TODO: добавить индикацию ошибки загрузки модуля
            console.warn('ERROR DURING INIT APP:', application.name);
            console.error(error);
            store.commit('incrementProcessedServices', redirect);
        })
};

export const initMicroFronts = (authData: IAuthData, redirect: boolean) => {
    requestConfig()
        .then((config) => {
            if ( config.common ) store.commit('setSystems', config.common.systems);
            const { groups } = authData;
            GlobalStore.commit('setApplications', config.applications); 
            const filtered: IApplication[] = config.applications.filter(
                (eachApp) => eachApp.roles.filter(
                    (eachRole) => groups.find( ( group ) => group.roles.includes(eachRole) )
                ).length && !eachApp.onDemand
            );
            store.commit('addTotalServices', filtered.length);

            config.applications.forEach( (application) => {
                if ( typeof application.onDemand !== 'undefined' && application.onDemand )
                    store.commit('addOnDemandApplication', application);
                else if ( application.roles.filter( eachRole => groups.find( ( group ) => group.roles.includes(eachRole) ) ).length > 0 )
                    registerMicroFront(authData, application, config, redirect);
            });

            // start();
        });
};

const collectStartPoints = (menuItem: IMenuBaseEntity, startPoints: IMenuBaseEntity[]) => {
    if ( !menuItem.subMenus || menuItem.subMenus.length === 0 ) {
        if ( menuItem.startPoint ) startPoints.push( menuItem );
        return;
    }

    menuItem.subMenus.forEach( (subMenuItem) => {
        collectStartPoints(subMenuItem, startPoints);
    } );
}

const getFirstMenu = (menuItem: IMenuBaseEntity): IMenuBaseEntity => {
    if ( menuItem.subMenus && menuItem.subMenus.length > 0 )
        return getFirstMenu( menuItem.subMenus[0] );

    return menuItem;
}

export const redirectToFirstMenu = (menus: IMenuEntity[]) => {
    if ( !menus || menus.length === 0 ) return;
    const startPoints: IMenuBaseEntity[] = [];

    menus.forEach( (menuItem) => {
        collectStartPoints(menuItem, startPoints);
    });

    if (startPoints.length) {
        startPoints.sort( (a, b) => {
            if (a.startPoint < b.startPoint) return -1;
            if (a.startPoint > b.startPoint) return 1;
            return 0;
        })
    }

    const firstMenu: IMenuBaseEntity = getFirstMenu( menus[0] );
    let targetRoute = firstMenu.path;

    if (startPoints.length && startPoints[0].path) {
        targetRoute = startPoints[0].path;
    }

    if ( router.currentRoute.fullPath !== targetRoute )
        router.push(targetRoute);
};

export const startMicroFronts = (redirect: boolean, menus: IMenuEntity[]) => {
    start();
    const theme = localStorage.getItem('theme');
    if (theme) setGlobalThemeType(theme as ThemeType);

    if ( redirect )
        redirectToFirstMenu(menus);
};
